import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchData } from 'services/api/fetchData';
import { saveData } from 'services/api/saveData';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useModal } from 'components/modals/ModalContext';
import { useGlobalContext } from 'GlobalContext';
import { useActiveFeature, useAllowedRight } from 'services/permissions/permissionChecks';
import IconButton from 'components/buttons/IconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import InvoiceDetailList from '../../views/invoices/InvoiceDetailList';
import InvoiceTemplateModal from '../../views/invoices/InvoiceTemplateModal';
import InvoiceScheduleCard from '../../views/invoices/InvoiceScheduleCard';
import { InvoiceTemplateType } from '../../views/invoices/InvoiceTypes';
import '../../style/scss/card.scss';

const ContractInvoicesTab: React.FC<{
    itemId: string;
    data?: any;
    refetchData?: () => void;
}> = ({
    itemId,
    data,
    refetchData,
}) => {
    const { t } = useTranslation();
    const { handleLogout } = useAuthContext();
    const { initializeModal } = useModal();
    const { setFloatingAlert } = useGlobalContext();
    const userHasOnlyViewRights = useAllowedRight('only_view');
    const periodicInvoicesActive = useActiveFeature('periodic_invoices');
    const [template, setTemplate] = useState<InvoiceTemplateType | null>(null);
    const [loaded, setLoaded] = useState(false);
    const [creating, setCreating] = useState(false);
    const [hasInvoices, setHasInvoices] = useState(false);
    const [invoiceListKey, setInvoiceListKey] = useState(0);

    // Fetch the periodic invoice of the contract
    const fetchTemplate = useCallback(async () => {
        const response = await fetchData({
            apiUrl: 'get_invoice_template_list',
            params: { contract: itemId, deleted: false },
            handleLogout
        });
        setTemplate(response.results?.[0] ?? null);
        setLoaded(true);
    }, [itemId]);

    // Determine whether the contract has invoices to show the invoice list for
    const fetchHasInvoices = useCallback(async () => {
        const response = await fetchData({
            apiUrl: 'get_invoice_list',
            params: { contract: itemId, deleted: false, page_size: 1 },
            handleLogout
        });
        setHasInvoices((response.count || 0) > 0);
    }, [itemId]);

    // Load the periodic invoice and the invoice count when opening the tab
    useEffect(() => {
        fetchTemplate();
        fetchHasInvoices();
    }, [fetchTemplate, fetchHasInvoices]);

    // Refresh the tab and the contract page after a change in the modal
    const handleChanged = () => {
        fetchTemplate();
        fetchHasInvoices();
        setInvoiceListKey(previousKey => previousKey + 1);
        if (refetchData) refetchData();
    };

    // Open the base invoice of the periodic invoice
    const openTemplate = (templateId: number) => {
        initializeModal(
            <InvoiceTemplateModal itemId={templateId} refetchList={handleChanged} />,
            { modalSize: 'large', itemId: templateId }
        );
    };

    // Create the periodic invoice of the contract and open its base invoice
    const createTemplate = async () => {
        if (creating) return;
        setCreating(true);

        // The first invoice falls on the start date of the contract, or today for a running contract
        const today = new Date().toISOString().split('T')[0];
        const firstInvoiceDate = data?.start_date && data.start_date > today ? data.start_date : today;

        try {
            const response = await saveData({
                apiUrl: 'post_invoice_template',
                method: 'post',
                data: {
                    contract: itemId,
                    interval_type: 'monthly',
                    first_invoice_date: firstInvoiceDate,
                },
            });
            if (response && response.status === 201) {
                setFloatingAlert({ type: 'success', message: 'validation.invoice_template.created_successfully' });
                handleChanged();
                openTemplate(response.data.id);
            }
        } catch (error) {
            console.error('Creating the periodic invoice failed', error);
        } finally {
            setCreating(false);
        }
    };

    return (
        <div className='contract-invoices'>
            {loaded && periodicInvoicesActive && (
                <div className='card linked-items-card contract-invoice-card'>
                    <div className='header'>
                        <h6>{t('invoice.schedule.card_label')}</h6>
                        {!template && !userHasOnlyViewRights && (
                            <IconButton
                                onClick={createTemplate}
                                size='extra-small'
                                icon={<FontAwesomeIcon icon={faPlus} />}
                                tooltipText={t('invoice.schedule.add_cycle_button_label')}
                            />
                        )}
                    </div>
                    {template && (
                        <InvoiceScheduleCard
                            data={template}
                            onClick={() => openTemplate(template.id)}
                        />
                    )}
                </div>
            )}
            {hasInvoices && (
                <InvoiceDetailList
                    key={invoiceListKey}
                    itemId={itemId}
                    detailPage='contract'
                    data={data}
                />
            )}
        </div>
    );
};

export default ContractInvoicesTab;
